/* eslint new-cap: ["error", { "newIsCap": false }] */
import moment from 'moment';
// The app's own Roboto. Regular carries Cyrillic and accented Latin, so names like "0002(Кеш)" print
// as typed — jsPDF's built-in Helvetica would garble them. The bold cut is Latin-only, so it is kept
// for the headers and the title.
import robotoRegularUrl from 'assets/fonts/Roboto/Roboto-Regular.ttf';
import robotoBoldUrl from 'assets/fonts/Roboto/Roboto-700.ttf';

// Brand colours (UiColors): forest text, dim-grey header text, cloud header fill, stroke rules.
const FOREST = [43, 78, 86];
const DIM_GREY = [96, 96, 96];
const CLOUD = [247, 242, 240];
const STROKE = [226, 222, 220];

const MARGIN = 10; // mm
const PAGE_TOP = 16; // mm — below the running title on every page
const FOOTER = 10; // mm

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
};

const loadFont = async (url) => toBase64(await (await fetch(url)).arrayBuffer());

/** Registers Roboto on the document; false (and Helvetica stays) if the font can't be fetched. */
const registerFonts = async (doc) => {
  try {
    const [regular, bold] = await Promise.all([loadFont(robotoRegularUrl), loadFont(robotoBoldUrl)]);
    doc.addFileToVFS('Roboto-Regular.ttf', regular);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFileToVFS('Roboto-700.ttf', bold);
    doc.addFont('Roboto-700.ttf', 'Roboto', 'bold');
    return true;
  } catch {
    return false;
  }
};

// Wide tables (most list pages) read better across the page.
const orientationFor = (columns) => {
  const pixels = columns.reduce((sum, col) => sum + (col.width || 0), 0);
  return columns.length > 5 || pixels > 900 ? 'landscape' : 'portrait';
};

/**
 * Lays a table read by readVisibleTable out as a real PDF table: text rather than a screenshot, the
 * header row repeated on every page, and no row ever cut in two — a row that doesn't fit moves to the
 * next page whole. Column widths keep the on-screen proportions.
 *
 * Every page carries the title and the print date; the first also lists the applied filters; the
 * footer numbers the pages.
 */
const buildTablePdf = async ({ table, title, filters = [], date = new Date() }) => {
  // Lazy: keep jsPDF (~330 kB) and the table plugin out of the main bundle until someone exports.
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const doc = new jsPDF({ orientation: orientationFor(table.columns), unit: 'mm', format: 'a4', compress: true });
  const font = (await registerFonts(doc)) ? 'Roboto' : 'helvetica';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const printedOn = `Printed on ${moment(date).format('MM/DD/YYYY')}`;

  // First page: the filters under the title, wrapped to the page width.
  let startY = PAGE_TOP;
  if (filters.length) {
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(`Filters: ${filters.join(' · ')}`, contentWidth);
    doc.setTextColor(...DIM_GREY);
    doc.text(lines, MARGIN, PAGE_TOP);
    startY = PAGE_TOP + lines.length * 3.6 + 2;
  }

  const totalPixels = table.columns.reduce((sum, col) => sum + (col.width || 1), 0) || 1;
  const columnStyles = Object.fromEntries(
    table.columns.map((col, index) => [index, { cellWidth: (contentWidth * (col.width || 1)) / totalPixels }]),
  );

  const totalPagesExp = '{total_pages}';

  autoTable(doc, {
    head: [table.columns.map((col) => col.label)],
    body: table.rows,
    startY,
    margin: { top: PAGE_TOP, bottom: FOOTER + 4, left: MARGIN, right: MARGIN },
    tableWidth: contentWidth,
    showHead: 'everyPage',
    // A row that doesn't fit moves to the next page whole (one taller than a page still splits).
    rowPageBreak: 'avoid',
    theme: 'grid',
    styles: {
      font,
      fontStyle: 'normal',
      fontSize: 8,
      cellPadding: 1.8,
      overflow: 'linebreak',
      valign: 'top',
      textColor: FOREST,
      lineColor: STROKE,
      lineWidth: 0.1,
    },
    headStyles: { font, fontStyle: 'bold', fontSize: 7, fillColor: CLOUD, textColor: DIM_GREY, valign: 'middle' },
    columnStyles,
    didDrawPage: () => {
      doc.setFont(font, 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...FOREST);
      doc.text(title, MARGIN, 10);
      doc.setFont(font, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...DIM_GREY);
      doc.text(printedOn, pageWidth - MARGIN, 10, { align: 'right' });
      // Built-in Helvetica: the total-pages placeholder is only swapped reliably in a standard font,
      // and this line is plain Latin anyway.
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${doc.internal.getNumberOfPages()} of ${totalPagesExp}`, pageWidth - MARGIN, pageHeight - 6, {
        align: 'right',
      });
    },
  });

  if (typeof doc.putTotalPages === 'function') doc.putTotalPages(totalPagesExp);
  return doc;
};

export default buildTablePdf;
